import { KeyRound, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../../../api/client';
import { FieldError } from '../../ui/FieldError';
import { translateApiError } from '../../../lib/errors';

interface RecoveryProps {
  onSuccess: (token: string) => Promise<void>;
}

export function AnonymousRecovery({ onSuccess }: RecoveryProps) {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!code.trim()) {
      setError('Введите код восстановления.');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await client.account.recoverAnonymous({ code: code.trim() });
      await onSuccess(response.token);
      setCode('');
    } catch (requestError) {
      setError(
        translateApiError(requestError),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-8 border-t border-purple-100 pt-8">
      <div className="flex items-start gap-3 mb-4">
        <div className="mt-0.5 rounded-xl bg-purple-100 p-2 text-purple-600">
          <KeyRound className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">Восстановить из кода</h3>
          <p className="mt-1 text-sm leading-5 text-gray-500">
            Введите код, который вы сохранили ранее, чтобы вернуть доступ к своим
            песням и балансам.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <textarea
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
            setError('');
          }}
          rows={3}
          placeholder="Введите код восстановления"
          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 font-mono text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
          aria-label="Код восстановления"
        />
        <FieldError message={error} />
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-xl border-2 border-purple-200 bg-purple-50 px-6 py-3 font-semibold text-purple-700 transition-colors hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Восстанавливаем…' : 'Восстановить'}
        </button>
      </form>

      <div className="mt-4 flex gap-2 rounded-xl bg-gray-50 p-3 text-xs leading-5 text-gray-500">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
        <span>Код одноразовый: после успешного восстановления его повторно выпускать не нужно.</span>
      </div>
    </div>
  );
}
